import {
  AGENT_TYPES,
  embeddingModels,
  enricherLangchainModels,
  linkingLangchainModels,
  showModelSelector as showModelSelectorEnv,
  unstructuredDataLoaderAgentTypes,
  type ModelOption,
} from '@/config/models.config';
import type {
  LLMConfiguration,
  ModelRegistry,
} from '@/interfaces/llmConfiguration.interface';

/**
 * Resolve a list of model ids against the model registry to ModelOption[].
 * Ids not present in the registry are dropped (step 4 of the LLM-config
 * source-of-truth migration).
 */
export const resolveModelOptions = (
  modelIds: string[] | undefined | null,
  registry: ModelRegistry,
): ModelOption[] => {
  if (!Array.isArray(modelIds)) return [];
  return modelIds
    .filter(
      (id): id is string =>
        typeof id === 'string' && Boolean(registry[id]?.label),
    )
    .map((id) => ({ value: id, label: registry[id].label }));
};

/**
 * Shape consumers see: one ModelOption[] per sub-system, ready to drop
 * into a PrimeReact Dropdown. `unstructuredDataLoader` keeps the
 * per-agent-type map so entity-workshop can look up by AgentType.
 *
 * Reasoner is intentionally absent — the reasoner block from the API is
 * ignored and ReasonerAgentSettingsDialog stays on its static catalog
 * (it carries rich agent params the registry endpoint doesn't model).
 */
export interface ResolvedLLMConfig {
  enricherModels: ModelOption[];
  linkingModels: ModelOption[];
  embeddingsModels: ModelOption[];
  unstructuredDataLoaderModels: Record<string, ModelOption[]>;
  showModelSelector: boolean;
}

/**
 * Normalize the raw API payload into ResolvedLLMConfig.
 *
 * `enricher.defaultProvider` / `linking.defaultProvider` select WHICH
 * provider block drives the dropdown — the pre-selected model id keeps
 * coming from the env defaults (VITE_ENRICHER_DEFAULT_MODEL etc.).
 *
 * No structural fallback here: when an entire sub-system list resolves
 * empty (e.g. all ids unknown, missing block, malformed payload) the
 * caller is expected to substitute the static default. This helper just
 * does the join + drop-bad-ids step.
 */
export const resolveLLMConfiguration = (
  raw: LLMConfiguration,
): ResolvedLLMConfig => {
  const registry: ModelRegistry =
    raw.modelRegistry && typeof raw.modelRegistry === 'object'
      ? raw.modelRegistry
      : {};

  const enricherProvider =
    raw.enricher?.providers?.[raw.enricher?.defaultProvider];
  const linkingProvider =
    raw.linking?.providers?.[raw.linking?.defaultProvider];

  const unstructuredAgentTypes = raw.unstructuredDataLoader?.agentTypes ?? {};
  const unstructuredDataLoaderModels: Record<string, ModelOption[]> = {};
  for (const [agentType, cfg] of Object.entries(unstructuredAgentTypes)) {
    unstructuredDataLoaderModels[agentType] = resolveModelOptions(
      cfg?.models,
      registry,
    );
  }

  return {
    enricherModels: resolveModelOptions(enricherProvider?.models, registry),
    linkingModels: resolveModelOptions(linkingProvider?.models, registry),
    embeddingsModels: resolveModelOptions(raw.embeddings?.models, registry),
    unstructuredDataLoaderModels,
    showModelSelector:
      typeof raw.showModelSelector === 'boolean'
        ? raw.showModelSelector
        : showModelSelectorEnv === 'true',
  };
};

/**
 * ResolvedLLMConfig built from the static models.config catalog. Used as
 * the fallback when the LLM config service errors or returns an empty
 * payload (step 3) and as the per-sub-system substitute when a resolved
 * list ends up empty (step 4).
 */
export const buildDefaultLLMConfig = (): ResolvedLLMConfig => ({
  enricherModels: enricherLangchainModels,
  linkingModels: linkingLangchainModels,
  embeddingsModels: embeddingModels,
  unstructuredDataLoaderModels: {
    [AGENT_TYPES.LANGCHAIN]:
      unstructuredDataLoaderAgentTypes[AGENT_TYPES.LANGCHAIN].models,
    [AGENT_TYPES.LANGCHAIN_TXT]:
      unstructuredDataLoaderAgentTypes[AGENT_TYPES.LANGCHAIN_TXT].models,
    [AGENT_TYPES.LANGCHAIN_JSON]:
      unstructuredDataLoaderAgentTypes[AGENT_TYPES.LANGCHAIN_JSON].models,
  },
  showModelSelector: showModelSelectorEnv === 'true',
});

/**
 * `true` when the API payload is missing or carries no usable model
 * registry — in that case the caller should fall back to
 * `buildDefaultLLMConfig()` rather than try to resolve.
 */
export const isLLMConfigurationEmpty = (
  raw: LLMConfiguration | null | undefined,
): boolean => {
  if (raw == null) return true;
  const registry = raw.modelRegistry;
  if (registry == null || typeof registry !== 'object') return true;
  return Object.keys(registry).length === 0;
};

/**
 * Augment a resolved option list with the saved value as a synthetic
 * option (label = id) when the saved id isn't already in the list.
 *
 * Used by edit flows so a previously-saved model that's no longer in
 * the API's modelRegistry still appears pre-selected — the user keeps
 * visibility of their choice and can re-pick or leave it alone.
 */
export const ensureModelOption = (
  options: ModelOption[],
  savedValue: string | undefined | null,
): ModelOption[] => {
  if (!savedValue) return options;
  if (options.some((o) => o.value === savedValue)) return options;
  return [...options, { value: savedValue, label: savedValue }];
};

/**
 * Per-sub-system safety net (step 4): any sub-system list that resolved
 * to empty (e.g. block missing on the payload, or every id mismatched
 * the registry) is replaced with the static default for that sub-system.
 *
 * Extra agent types the API surfaced that aren't in the static defaults
 * are preserved as-is when non-empty, so a future LangChain* variant
 * doesn't need a frontend change to appear.
 */
export const applySubsystemDefaults = (
  resolved: ResolvedLLMConfig,
): ResolvedLLMConfig => {
  const defaults = buildDefaultLLMConfig();

  const unstructured: Record<string, ModelOption[]> = {};
  for (const [agentType, defaultModels] of Object.entries(
    defaults.unstructuredDataLoaderModels,
  )) {
    const resolvedModels = resolved.unstructuredDataLoaderModels[agentType];
    unstructured[agentType] =
      resolvedModels && resolvedModels.length > 0
        ? resolvedModels
        : defaultModels;
  }
  for (const [agentType, models] of Object.entries(
    resolved.unstructuredDataLoaderModels,
  )) {
    if (!(agentType in unstructured) && models.length > 0) {
      unstructured[agentType] = models;
    }
  }

  return {
    enricherModels:
      resolved.enricherModels.length > 0
        ? resolved.enricherModels
        : defaults.enricherModels,
    linkingModels:
      resolved.linkingModels.length > 0
        ? resolved.linkingModels
        : defaults.linkingModels,
    embeddingsModels:
      resolved.embeddingsModels.length > 0
        ? resolved.embeddingsModels
        : defaults.embeddingsModels,
    unstructuredDataLoaderModels: unstructured,
    showModelSelector: resolved.showModelSelector,
  };
};
